import React from "react";
import { ProductMap, Product } from "./types";
import { Button, CircularProgress } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import vanIcon from "../../../assets/image/svg/van.svg";
import { useEffect, useState } from "react";
import { customStyle } from "../../../features/viewOrder/ViewOrderUtils";
import { ReactComponent as PdfIcon } from "../../../assets/image/svg/pdfIcon.svg";

const defaultTotalInfo = {
  numberOfProducts: 0,
  totalCaseQuantity: 0,
  totalPieceQuantity: 0,
  totalOtherUnitQuantity: 0,
  totalAmount: 0,
};

interface CartSummaryFooterProps {
  cartProducts: ProductMap | null;
  onButtonClick: (value?: string) => void;
  onDeleteButtonClick?: (value?: string) => void;
  buttonText: string;
  endIcon?: boolean;
  reviewLoadout?: boolean;
  disabled?: boolean;
  totalAmountAndQtyInfo?: any;
  loading?: boolean;
  rsu?: boolean;
  displaySaveButton?: boolean;
  displayDeleteButton?: boolean;
}

const LoadoutCartSummaryFooter: React.FC<CartSummaryFooterProps> = ({
  cartProducts,
  onButtonClick = () => {},
  onDeleteButtonClick = () => {},
  buttonText = "Submit",
  endIcon = false,
  reviewLoadout = false,
  disabled = false,
  totalAmountAndQtyInfo = {},
  loading = false,
  rsu = false,
  displaySaveButton = false,
  displayDeleteButton = false,
}) => {
  const [totalInfo, setTotalInfo] =
    useState<Record<string, number>>(defaultTotalInfo);

  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const { caseUnitLabel, otherUnitLabel, packUnitLabel, currency } =
    ConfigDictionary;

  const calculateTotalAmountAndQuantities = (products: Product[]) => {
    let totalAmount = 0;
    let totalCaseQuantity = 0;
    let totalPieceQuantity = 0;
    let totalOtherUnitQuantity = 0;

    products?.forEach((product) => {
      const {
        pieceQuantity,
        caseQuantity,
        otherUnitQuantity,
        price,
        casePrice,
        otherUnitPrice,
      } = product;
      totalAmount +=
        pieceQuantity * price +
        caseQuantity * casePrice +
        otherUnitQuantity * otherUnitPrice;
      totalCaseQuantity += caseQuantity;
      totalPieceQuantity += pieceQuantity;
      totalOtherUnitQuantity += otherUnitQuantity;
    });

    return {
      numberOfProducts: products?.length,
      totalCaseQuantity,
      totalPieceQuantity,
      totalOtherUnitQuantity,
      totalAmount,
    };
  };

  useEffect(() => {
    if (Object.keys(totalAmountAndQtyInfo).length > 0) {
      setTotalInfo(totalAmountAndQtyInfo);
    } else {
      const totalData = calculateTotalAmountAndQuantities(
        Object.values(cartProducts ?? {})
      );
      setTotalInfo(totalData);
    }
  }, [cartProducts]);

  const formattedAmount = new Intl.NumberFormat("en-IN").format(
    Number(totalInfo?.totalAmount?.toFixed(2))
  );

  return (
    <div
      style={{
        height: rsu ? "25%" : "100%",
        boxShadow: !reviewLoadout ? "none" : "",
        alignItems: "flex-end",
        padding: reviewLoadout ? "8px 10px" : "",
      }}
      className="cart-summary"
    >
      <div className="cart-info" style={{ alignItems: "flex-start" }}>
        <div className="checkout-cart-icon">
          <img alt="cart-icon" src={vanIcon} height="32px" width="70%" />
          <span
            className="checkout-cart-count"
            style={{
              top: "-6px",
              right: "2px",
            }}
          >
            {totalInfo.numberOfProducts}
          </span>
        </div>
        {totalInfo.numberOfProducts > 0 && (
          <div
            style={{
              fontSize: "16.8px",
              letterSpacing: "-0.44px",
              marginLeft: "30px",
            }}
            className="cart-details"
          >
            <div>
              Total Quantity:{" "}
              <strong>
                {totalInfo.totalCaseQuantity} {caseUnitLabel},{" "}
              </strong>
              {otherUnitLabel && (
                <strong>
                  {totalInfo.totalOtherUnitQuantity} {otherUnitLabel},{" "}
                </strong>
              )}
              <strong>
                {totalInfo.totalPieceQuantity} {packUnitLabel}
              </strong>
            </div>
            <div>
              Total Amount:{" "}
              <strong>
                {currency} {formattedAmount}
              </strong>
            </div>
          </div>
        )}
      </div>

      <div>
        {(reviewLoadout || displayDeleteButton) && (
          <Button
            variant="outlined"
            size={displayDeleteButton ? "medium" : "large"}
            endIcon={
              endIcon &&
              !displayDeleteButton && (
                <ArrowForwardIosIcon sx={{ height: "15px", width: "15px" }} />
              )
            }
            sx={{
              ":hover": {
                boxShadow: "none",
                border: displayDeleteButton
                  ? "1px solid #c64c01"
                  : "1px solid #02C6B1",
              },
              textTransform: "none",
              boxShadow: "none",
              border: displayDeleteButton
                ? "1px solid #c64c01"
                : "1px solid #02C6B1",
              color: displayDeleteButton ? "#c64c01" : "#02C6B1",
              mr: displayDeleteButton ? "15px" : "10px",
              borderRadius: "8px",
              fontWeight: 600,
            }}
            onClick={() => {
              if (!loading) {
                if (displayDeleteButton) {
                  onDeleteButtonClick();
                } else {
                  onButtonClick("IN-DRAFT");
                }
              }
            }}
          >
            {!displaySaveButton && !displayDeleteButton && <PdfIcon />}
            {!displaySaveButton && "\u00A0\u00A0"}
            {displayDeleteButton
              ? "Delete Loadout"
              : displaySaveButton
              ? "Save Progress"
              : "Print Loadslip"}
          </Button>
        )}
        {buttonText.length > 0 && (
          <Button
            variant="contained"
            endIcon={
              endIcon &&
              !loading && (
                <ArrowForwardIosIcon
                  sx={{
                    height: !reviewLoadout ? "13px" : "15px",
                    width: "15px",
                    marginLeft: !reviewLoadout ? "-4px !important" : "",
                  }}
                />
              )
            }
            sx={{
              backgroundColor: "var(--dashboard-theme-color)",
              ":hover": {
                background: "var(--dashboard-theme-color)",
                boxShadow: "none",
              },
              textTransform: "none",
              boxShadow: "none",
              borderRadius: "8px",
              "&.Mui-disabled": {
                color: "#7C7C7C",
                fontWeight: 600,
              },
              minWidth: "130px",
              minHeight: "37px",
            }}
            onClick={loading ? () => {} : () => onButtonClick()}
            size={reviewLoadout ? "large" : rsu ? "small" : "medium"}
            disabled={disabled}
          >
            {loading ? <CircularProgress style={customStyle} /> : buttonText}
          </Button>
        )}
      </div>
    </div>
  );
};

export default LoadoutCartSummaryFooter;
